import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CardComponent } from '@custom-components/general/card';
import {
  DonutChartComponent,
  DonutChartSegment,
} from '@custom-components/general/donut-chart/donut-chart.component';

const SEGMENT_LABEL_KEYS: Record<string, string> = {
  Online: 'home.devicesWidget.online',
  Offline: 'home.devicesWidget.offline',
  Installed: 'home.devicesWidget.installed',
  'To be installed': 'home.devicesWidget.toBeInstalled',
  'Installation failed': 'home.devicesWidget.installationFailed',
};

@Component({
  selector: 'xemex-dashboard-devices-widget',
  imports: [CardComponent, DonutChartComponent, TranslateModule],
  styleUrl: './dashboard-devices-widget.component.scss',
  templateUrl: './dashboard-devices-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardDevicesWidgetComponent {
  /** Online, Offline, Installation failed, To be installed (D499). */
  segments = input.required<DonutChartSegment[]>();
  /** Online count, or a dash when no device is installed. */
  centerLabel = input.required<string>();
  /** Installed devices (Online + Offline) for "online of <n>"; null when the count failed. */
  installedTotal = input<number | null>(null);

  viewAll = output<void>();
  segmentFilterClick = output<string>();

  protected readonly hovered = signal<string | null>(null);
  /** Hovered group header: its segments are highlighted together in the ring. */
  protected readonly hoveredGroup = signal<string[] | null>(null);
  protected readonly highlighted = computed(() => this.hoveredGroup() ?? this.hovered());

  /** Legend in two groups with their totals: registered (Online, Offline) and not registered. */
  protected readonly groups = computed(() => {
    const group = (key: string, rows: DonutChartSegment[]) => ({
      key,
      rows,
      total: rows.reduce((sum, row) => sum + row.value, 0),
    });
    const registered = this.segments().filter(s => s.label === 'Online' || s.label === 'Offline');
    const rest = this.segments().filter(s => !registered.includes(s));
    return [
      group('home.devicesWidget.registered', registered),
      group('home.devicesWidget.notRegistered', rest),
    ].filter(g => g.rows.length);
  });

  /** All segments together: the visible device total. */
  protected readonly total = computed(() => this.segments().reduce((sum, s) => sum + s.value, 0));

  protected groupLabels(group: { rows: DonutChartSegment[] }): string[] {
    return group.rows.map(row => row.label);
  }

  protected isHighlighted(label: string): boolean {
    const highlighted = this.highlighted();
    return highlighted != null && [highlighted].flat().includes(label);
  }

  protected labelKey(label: string): string {
    return SEGMENT_LABEL_KEYS[label] ?? label;
  }
}
